// frontend/src/pages/Login.tsx

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/useAuthStore';
import api from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ResendVerificationButton } from '@/components/ui/ResendVerificationButton';

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || "/dashboard";
  const { login } = useAuthStore();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  // Email two-factor step (admin only): email/password are kept in formData
  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(60);
    try {
      await api.post('/auth/2fa/resend', { email: formData.email, password: formData.password });
      toast.success('If a code is required, a new one has been sent.');
    } catch {
      // api interceptor already shows the error toast
    }
  };

  const cancelTwoFactor = () => {
    setTwoFactorStep(false);
    setEmailCode('');
    setCodeError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setUnverifiedEmail('');
    setCodeError('');

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.email) {
      newErrors.email = t('auth.email_required');
    }
    if (!formData.password) {
      newErrors.password = t('auth.password_required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (twoFactorStep && !/^\d{6}$/.test(emailCode)) {
      setCodeError('Enter the 6-digit code from your email.');
      return;
    }

    try {
      setIsLoading(true);
      await login(twoFactorStep ? { ...formData, emailCode } : formData);
      toast.success(t('auth.login_success'));
      navigate(from, { replace: true });
    } catch (error: any) {
      const code = error?.response?.data?.code;
      if (code === 'TWO_FACTOR_REQUIRED') {
        setTwoFactorStep(true);
        setEmailCode('');
        setResendCooldown(60);
      } else if (code === 'TWO_FACTOR_INVALID') {
        setEmailCode('');
        setCodeError('Incorrect or expired code. Try again, or resend a new code.');
      } else if (code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(formData.email);
        toast.error(t('auth.email_not_verified'));
      } else {
        toast.error(t('auth.invalid_credentials'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-apple-blue-50 to-white py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Zap className="w-8 h-8 text-apple-blue-500" />
          <span className="text-2xl font-semibold">{t('app_name')}</span>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-3xl">
              {t('auth.login')}
            </CardTitle>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {twoFactorStep ? (
                <>
                  <p className="text-apple-gray-700">We emailed a code to your address.</p>

                  <Input
                    label="Email code"
                    type="text"
                    inputMode="numeric"
                    placeholder="123456"
                    maxLength={6}
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    error={codeError}
                    autoComplete="one-time-code"
                    autoFocus
                  />

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resendCooldown > 0}
                      className="text-sm text-apple-blue-500 hover:text-apple-blue-600 disabled:text-apple-gray-400 disabled:cursor-not-allowed"
                    >
                      {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelTwoFactor}
                      className="text-sm text-apple-gray-600 hover:text-apple-gray-800"
                    >
                      Use a different account
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Input
                    label={t('auth.email')}
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    error={errors.email}
                    autoComplete="email"
                  />

                  <Input
                    label={t('auth.password')}
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    error={errors.password}
                    autoComplete="current-password"
                  />

                  <div className="flex items-center justify-between">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-apple-blue-500 hover:text-apple-blue-600"
                    >
                      {t('auth.forgot_password')}
                    </Link>
                  </div>
                </>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                isLoading={isLoading}
              >
                {twoFactorStep ? 'Confirm' : t('auth.login')}
              </Button>
            </form>

            {unverifiedEmail && (
              <div className="mt-6 space-y-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <p className="text-sm text-yellow-800">{t('auth.email_not_verified')}</p>
                <ResendVerificationButton email={unverifiedEmail} />
              </div>
            )}

            <div className="mt-6 text-center">
              <span className="text-apple-gray-600">
                {t('auth.dont_have_account')}{' '}
              </span>
              <Link
                to="/signup"
                className="text-apple-blue-500 hover:text-apple-blue-600 font-medium"
              >
                {t('auth.signup')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
